import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './DiscountPlaces.css';

const DiscountPlaces = () => {
  usePageTitle('Discount Places');
  const { user } = useAuth();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPlaces();
  }, []);

  const fetchPlaces = async () => {
    try {
      const response = await api.get('/discount-places');
      
      // Handle different response structures
      let placesData = response.data;
      
      // Check if response is HTML (routing issue)
      if (typeof placesData === 'string' && placesData.trim().startsWith('<!')) {
        console.error('API returned HTML instead of JSON');
        placesData = [];
      } else if (!Array.isArray(placesData)) {
        // If it's an object with a data property, use that
        if (placesData && Array.isArray(placesData.data)) {
          placesData = placesData.data;
        } else {
          placesData = [];
        }
      }
      
      setPlaces(placesData);
    } catch (error) {
      console.error('Error fetching discount places:', error);
      console.error('Error details:', error.response?.data || error.message);
      setPlaces([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceClick = (place) => {
    // Only allow authenticated users to see promo code
    if (!user) {
      alert('Please login first to view promo code');
      return;
    }

    setSelectedPlace(place);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPlace(null);
  };

  const copyPromoCode = (promoCode) => {
    navigator.clipboard.writeText(promoCode).then(() => {
      alert('Promo code copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy promo code');
    });
  };

  const filteredPlaces = places.filter(place => {
    const searchLower = searchTerm.toLowerCase();
    return (
      place.place_name?.toLowerCase().includes(searchLower) ||
      place.promo_code?.toLowerCase().includes(searchLower) ||
      place.description?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading...</div>
      </div>
    );
  }

  return (
    <div className="discount-places-container">
      <div className="discount-places-header">
        <h1>Discount Places</h1>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by name or promo code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="discount-search-input"
          />
        </div>
      </div>
      {(!Array.isArray(filteredPlaces) || filteredPlaces.length === 0) ? (
        <div className="no-data">
          <p>{searchTerm ? 'No matches found for your search.' : 'No discount places available at the moment.'}</p>
        </div>
      ) : (
        <div className="places-grid">
          {filteredPlaces.map((place) => {
            // Default images for different types of places
            const defaultImages = [
              'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=200&fit=crop&q=80&auto=format', // Restaurant
              'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=200&fit=crop&q=80&auto=format', // Coffee shop
              'https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=400&h=200&fit=crop&q=80&auto=format', // Fast food
              'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=400&h=200&fit=crop&q=80&auto=format', // Bar
              'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=200&fit=crop&q=80&auto=format', // Restaurant 2
              'https://images.unsplash.com/photo-1501339847302-ac426a4c7c6e?w=400&h=200&fit=crop&q=80&auto=format', // Cafe
            ];
            // Use different image based on place ID for variety
            const defaultImageIndex = (place.id - 1) % defaultImages.length;
            const defaultImage = defaultImages[defaultImageIndex];
            
            const logoUrl = place.logo_url 
              ? `${place.logo_url}${place.logo_url.includes('?') ? '&' : '?'}t=${Date.now()}` 
              : (place.logo 
                ? `https://aliensteam.com/app-media/${place.logo}?t=${Date.now()}` 
                : defaultImage);
            
            return (
            <div 
              key={place.id} 
              className={`place-card ${user ? 'clickable' : ''}`}
              onClick={() => user && handlePlaceClick(place)}
              style={{ cursor: user ? 'pointer' : 'default' }}
            >
              <div className="place-image-wrapper">
                <img
                  src={logoUrl}
                  alt={place.place_name}
                  className="place-logo"
                  onError={(e) => {
                    const currentSrc = e.target.src;
                    const retryCount = parseInt(e.target.getAttribute('data-retry') || '0');
                    console.log(`Public Logo failed (retry ${retryCount}):`, currentSrc);
                    
                    if (retryCount < 4) {
                      e.target.setAttribute('data-retry', retryCount + 1);
                      
                      // Strategy 1: Try the App Media route (Hostinger Direct)
                      if (!currentSrc.includes('app-media')) {
                         // Extract the path after 'storage/' or 'logos/'
                         let path = '';
                         if (currentSrc.includes('/storage/')) {
                             path = currentSrc.split('/storage/')[1];
                         } else if (currentSrc.includes('/logos/')) {
                             path = 'logos/' + currentSrc.split('/logos/')[1];
                         }
                         
                         // Clean path
                         if (path.startsWith('app/public/')) path = path.replace('app/public/', '');
                         
                         if (path) {
                             e.target.src = `https://aliensteam.com/app-media/${path}?t=${Date.now()}`;
                             return;
                         }
                      }
                      
                      // Strategy 2: Toggle storage paths (Legacy)
                      if (currentSrc.includes('/storage/app/public/')) {
                        e.target.src = currentSrc.replace('/storage/app/public/', '/storage/');
                      } else if (currentSrc.includes('/storage/')) {
                        e.target.src = currentSrc.replace('/storage/', '/storage/app/public/');
                      } else {
                        e.target.src = defaultImage;
                      }
                    } else if (currentSrc !== defaultImage) {
                      e.target.src = defaultImage;
                    } else {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
                <div className="place-logo-placeholder" style={{ display: 'none' }}>
                  <div className="placeholder-icon">🏪</div>
                  <div className="placeholder-text">{place.place_name}</div>
                </div>
              </div>
            <div className="place-content">
              <h3>{place.place_name}</h3>
              <div className="discount-badge">
                {place.discount_percentage}% OFF
              </div>
              {place.description && (
                <p className="place-description">{place.description}</p>
              )}
              {user && (
                <div className="promo-code-hint">
                  Click to view promo code
                </div>
              )}
            </div>
          </div>
          );
          })}
        </div>
      )}

      {/* Promo Code Modal */}
      {showModal && selectedPlace && (
        <div className="promo-modal-overlay" onClick={closeModal}>
          <div className="promo-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="promo-modal-close" onClick={closeModal}>×</button>
            <h2 className="promo-modal-title">{selectedPlace.place_name}</h2>
            
            <div className="promo-modal-info">
              <div className="promo-discount-badge">
                {selectedPlace.discount_percentage}% OFF
              </div>
              {selectedPlace.description && (
                <p className="promo-place-description">{selectedPlace.description}</p>
              )}
            </div>

            {selectedPlace.promo_code ? (
              <div className="promo-code-container">
                <p className="promo-code-label">Your Promo Code:</p>
                <div className="promo-code-display">
                  <span className="promo-code-text">{selectedPlace.promo_code}</span>
                  <button 
                    className="promo-code-copy-btn"
                    onClick={() => copyPromoCode(selectedPlace.promo_code)}
                    title="Copy to clipboard"
                  >
                    📋 Copy
                  </button>
                </div>
                <p className="promo-code-instructions">
                  Use this code at {selectedPlace.place_name} to get your discount
                </p>
              </div>
            ) : (
              <div className="promo-code-error">
                <p>No promo code available for this place</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountPlaces;
